import type { Role, SourceKind, TimelineEvent, TrackFinishRequest } from '../../../shared/protocol.ts';
import { api } from './api.ts';
import { SYNC_MARK_EVERY_S } from './recorder.ts';
import type { LocalStorageFiles } from './storage.ts';
import type { RecoveryDraft } from './uploader.ts';

/**
 * The take journal: this computer's own record of a take, kept next to the recordings in local storage.
 *
 * The local files are the source of truth, so the record of what they are must not depend on the server or on
 * the browser's localStorage. Each file is entered *before* recording starts, then kept up to date every few
 * seconds: its sync marks (where each 10-second instant of take time falls in the file), crash-recovery details,
 * and what happened (mutes, sources added, devices changed, reconnects). With the journal alone, files can be
 * recovered, registered, uploaded and lined up long after the take, even if the server never heard of them.
 */
export interface JournalFile {
  /** Name of the file in local storage; also its stable id when registering with the server. */
  localName: string;
  sourceId: string;
  kind: SourceKind;
  label: string;
  device?: string;
  ext: 'mp4' | 'wav';
  trackId: string | null;
  startedServerMs: number | null;
  state: 'recording' | 'done' | 'error';
  endedReason?: TrackFinishRequest['endedReason'];
  size?: number;
  /** Repair and placement details, refreshed while recording (includes the sync marks). */
  draft?: RecoveryDraft;
  /** The full timing manifest once the file is closed. */
  finish?: Omit<TrackFinishRequest, 'size'>;
}

export interface JournalData {
  format: 'takehouse-journal@1';
  studioId: string;
  studioName: string;
  /** This participant's studio link key: lets a later visit register files the server never saw. */
  key: string;
  takeId: string;
  takeNumber: number;
  takeStartServerMs: number;
  syncMarkEveryS: number;
  participantId: string;
  participantName: string;
  role: Role;
  browser: string;
  files: JournalFile[];
  events: Omit<TimelineEvent, 'takeId' | 'participantId' | 'participantName'>[];
  createdAt: number;
  updatedAt: number;
  closed: boolean;
  /** The server has the latest version. */
  uploadedAt?: number;
}

export const isJournalName = (name: string) => name.startsWith('journal-') && name.endsWith('.json');

export class TakeJournal {
  readonly name: string;
  data: JournalData;
  private storage: LocalStorageFiles;
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private saving: Promise<unknown> = Promise.resolve();
  private uploadTimer: ReturnType<typeof setInterval> | null = null;
  private dirtySinceUpload = true;

  constructor(storage: LocalStorageFiles, init: Omit<JournalData, 'format' | 'files' | 'events' | 'createdAt' | 'updatedAt' | 'closed' | 'syncMarkEveryS' | 'browser'>) {
    this.storage = storage;
    this.name = `journal-${init.takeId.slice(0, 8)}-${init.participantId.slice(0, 8)}.json`;
    const t = Date.now();
    this.data = { format: 'takehouse-journal@1', ...init, syncMarkEveryS: SYNC_MARK_EVERY_S, browser: navigator.userAgent, files: [], events: [], createdAt: t, updatedAt: t, closed: false };
  }

  /** Continue a journal already on disk (the same take after a reload or crash). */
  static async resume(storage: LocalStorageFiles, name: string): Promise<TakeJournal | null> {
    try {
      const data = JSON.parse(await storage.readText(name)) as JournalData;
      if (data?.format !== 'takehouse-journal@1') return null;
      const j = new TakeJournal(storage, data);
      j.data = { ...data, closed: false };
      // A fresh page has no recorders yet: anything still marked "recording" was cut off by the crash or reload.
      for (const f of j.data.files) {
        if (f.state === 'recording') Object.assign(f, { state: 'error', endedReason: 'error' });
      }
      return j;
    } catch {
      return null;
    }
  }

  /** Written straight away: a file is in the journal before its first byte is recorded. */
  async addFile(f: Omit<JournalFile, 'state' | 'trackId'> & { trackId?: string | null }) {
    this.data.files.push({ ...f, trackId: f.trackId ?? null, state: 'recording' });
    await this.saveNow();
  }

  updateFile(localName: string, patch: Partial<JournalFile>) {
    const f = this.data.files.find((x) => x.localName === localName);
    if (!f) return;
    Object.assign(f, patch);
    this.save();
  }

  addEvent(e: JournalData['events'][number]) {
    this.data.events.push(e);
    this.save();
  }

  /** Debounced write; the journal is small, so it is simply rewritten. */
  save() {
    this.dirtySinceUpload = true;
    if (this.saveTimer) return;
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      void this.saveNow();
    }, 1000);
  }

  async saveNow() {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = null;
    this.dirtySinceUpload = true;
    this.data.updatedAt = Date.now();
    const bytes = new TextEncoder().encode(JSON.stringify(this.data));
    this.saving = this.saving.then(() => this.storage.put(this.name, bytes)).catch((e) => console.warn('[takehouse] journal save failed', e));
    await this.saving;
  }

  /** Keep the server's copy fresh during the take, so it holds the journal even if this computer never returns. */
  startUploading(everyMs = 30_000) {
    this.uploadTimer ??= setInterval(() => void this.upload(), everyMs);
  }

  async upload() {
    if (!this.dirtySinceUpload) return true;
    this.dirtySinceUpload = false;
    try {
      await api.uploadJournal(this.data.takeId, this.data.key, this.data.participantId, this.data);
      this.data.uploadedAt = Date.now();
      await this.storage.put(this.name, new TextEncoder().encode(JSON.stringify(this.data)));
      return true;
    } catch {
      this.dirtySinceUpload = true;
      return false;
    }
  }

  /** End of take: final write, final upload (retried in the background if the server can't be reached). */
  async close() {
    if (this.uploadTimer) clearInterval(this.uploadTimer);
    this.uploadTimer = null;
    this.data.closed = true;
    await this.saveNow();
    for (let attempt = 0; attempt < 6; attempt++) {
      if (await this.upload()) return;
      await new Promise((r) => setTimeout(r, Math.min(30_000, 2000 * 2 ** attempt)));
    }
  }
}
